import * as _ from 'lodash';
import { useCallback, useState } from 'react';
import type { FocusEvent } from 'react';

const DEFAULT_CLOSE_DELAY = 100; // in ms

/**
 * Hook for managing the state of a dropdown. Includes handlers
 * for mouseenter, mouseleave, focus, and blur events.
 */
function useDropdown<El extends HTMLElement = HTMLDivElement>(delay: number = DEFAULT_CLOSE_DELAY) {
  const [isOpen, setIsOpen] = useState(false);
  const openDropdown = useCallback(() => setIsOpen(true), []);
  const closeDropdown = useCallback(() => setIsOpen(false), []);

  return {
    isOpen,
    handleMouseEnter: openDropdown,
    //  Delay here is to allow the user's mouse to move from the item to the dropdown without it closing.
    //  100ms was selected because it's imperceptible to the user, but gives them enough time to move their mouse.
    handleMouseLeave: useCallback(() => _.delay(closeDropdown, delay), [closeDropdown, delay]),
    handleFocus: openDropdown,
    handleBlur: useCallback((event: FocusEvent<El>) => {
      //  This ensures that the dropdown remains open so that it can be tabbed into via the keyboard.
      if (!event.currentTarget.contains(event.relatedTarget)) {
        closeDropdown();
      }
    }, [closeDropdown]),
  };
}

export default useDropdown;
